/**
 * Parses the agent's `envelope/v1` tool result into the evidence a citation
 * needs — where a value came from, when it was observed, and whether coverage
 * was partial (integration plan 05 C7, 2026-09-29). Returns null when the
 * result is not an envelope.
 *
 * Envelope shape is owned by langchain-agent `src/backend/envelope.py`:
 * `provenance` entries are `{source, source_url}` and observation time is
 * `freshness.as_of` (nullable) — there is no per-source `observed_at`.
 * Freshness is never invented; `coverage_status` is surfaced as the server set
 * it, never hidden. Nothing here throws.
 */
import { parseSafeHref } from '../components/chat/SafeMarkdownLink';

const ENVELOPE_VERSION = 'envelope/v1';
const MAX_SOURCES = 12;
const MAX_SOURCE_NAME = 80;
// Bound how many tool results one turn can attach, so a hostile/looping agent
// cannot grow a persisted message without limit.
export const MAX_TURN_EVIDENCE = 24;

function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function unwrapContent(content) {
  return Array.isArray(content) ? content[0] : content;
}

function parseSources(provenance) {
  if (!Array.isArray(provenance)) return { sources: [], extra: 0 };
  const all = [];
  for (const entry of provenance) {
    if (!isPlainObject(entry)) continue;
    const raw = typeof entry.source === 'string' ? entry.source.trim() : '';
    if (!raw) continue;
    const source = raw.length > MAX_SOURCE_NAME ? `${raw.slice(0, MAX_SOURCE_NAME)}…` : raw;
    const parsed = parseSafeHref(entry.source_url); // https-only URL or null
    // Never a userinfo URL (https://a@evil): its host is not what a reader
    // expects (review C7).
    const url = parsed && !parsed.username && !parsed.password ? parsed : null;
    all.push({ source, host: url ? url.hostname : null, url: url ? url.href : null });
  }
  return { sources: all.slice(0, MAX_SOURCES), extra: Math.max(0, all.length - MAX_SOURCES) };
}

function parseKind(value) {
  return value === 'reference' || value === 'model' ? value : 'observation';
}

function parseCoverage(value) {
  // The server sets one of these; a missing/renamed value fails CLOSED to
  // 'unknown' (a visible neutral badge), never to a clean 'ok' (review C7 M1).
  if (value === 'ok' || value === 'degraded' || value === 'missing') return value;
  return 'unknown';
}

/** @returns {null | {sources, extraSources, kind, observedAt, ageSeconds, window, coverage}} */
export function parseToolEvidence(content) {
  const value = unwrapContent(content);
  if (!isPlainObject(value) || value.envelope !== ENVELOPE_VERSION) return null;

  const { sources, extra } = parseSources(value.provenance);
  const freshness = isPlainObject(value.freshness) ? value.freshness : {};
  const observedAt = typeof freshness.as_of === 'string' ? freshness.as_of : null;
  const ageSeconds =
    typeof freshness.age_s === 'number' && Number.isFinite(freshness.age_s) ? freshness.age_s : null;
  const window = typeof freshness.window === 'string' ? freshness.window : null;

  return {
    sources,
    extraSources: extra,
    kind: parseKind(freshness.kind),
    observedAt,
    ageSeconds,
    window,
    coverage: parseCoverage(value.coverage_status),
  };
}

/** Whole-unit relative age, e.g. "12 min old"; null ⇒ caller shows
 *  "observation time unknown". */
export function humaniseAge(ageSeconds) {
  if (typeof ageSeconds !== 'number' || !Number.isFinite(ageSeconds) || ageSeconds < 0) return null;
  if (ageSeconds < 60) return 'just now';
  const mins = Math.floor(ageSeconds / 60);
  if (mins < 60) return `${mins} min old`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} hr old`;
  return `${Math.floor(hours / 24)} days old`;
}

/**
 * Attaches a turn's collected evidence to THIS turn's answer: the last
 * assistant message that comes after the last user message (so it is never
 * pinned to a previous turn's answer — review C7 M2). Returns `messages`
 * unchanged when there is nothing to attach (no evidence, or a tools-only turn
 * with no assistant answer after the user's message). Never mutates.
 * @param {Array} messages
 * @param {Array} evidence - parsed evidence objects from the turn
 */
export function attachEvidenceToAnswer(messages, evidence) {
  if (!Array.isArray(evidence) || evidence.length === 0) return messages;
  let lastUser = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].type === 'user') {
      lastUser = i;
      break;
    }
  }
  for (let i = messages.length - 1; i > lastUser; i -= 1) {
    const m = messages[i];
    if (m.type === 'assistant') {
      if (m.sources) return messages; // already cited; leave it
      const next = messages.slice();
      next[i] = { ...m, sources: evidence.slice(0, MAX_TURN_EVIDENCE) };
      return next;
    }
  }
  return messages;
}

/** Formats an envelope `as_of` (always `%Y-%m-%dT%H:%M:%SZ`) as
 *  "YYYY-MM-DD HH:MM UTC", locale-free and deterministic. Returns the raw
 *  string if it is not in that form, or null for null. */
export function formatObservedUtc(asOf) {
  if (typeof asOf !== 'string') return null;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(asOf);
  return m ? `${m[1]} ${m[2]} UTC` : asOf;
}
